import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { MagnifyingGlass, BookOpen, Wrench, Package, ArrowsClockwise, Check, Trash, X, Copy, DownloadSimple, UploadSimple, Tag } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { ArticleEditorModal } from '../invoicing/ArticleEditorModal';
import { MaterialFormModal } from '../invoicing/MaterialFormModal';
import { CategoryManagerModal } from '../categories/CategoryManagerModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Project tab: read-only view of the global articles library, filterable by category + search.
 * Click on a row opens the ArticleEditorModal in view/edit mode (admin can edit).
 */
export const ProjectLibraryTab = ({ project }) => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [editing, setEditing] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showMaterialForm, setShowMaterialForm] = useState(false);
  const [refreshingCoefs, setRefreshingCoefs] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkCategory, setBulkCategory] = useState('');
  const [showCategoryManager, setShowCategoryManager] = useState(false);

  const toggleSelectOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (rows) => {
    setSelectedIds(prev => {
      const allIds = rows.map(r => r.id);
      const allSelected = allIds.every(id => prev.has(id));
      if (allSelected) {
        const next = new Set(prev);
        allIds.forEach(id => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      allIds.forEach(id => next.add(id));
      return next;
    });
  };
  const exitSelectMode = () => { setSelectMode(false); setSelectedIds(new Set()); setBulkCategory(''); };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    if (!window.confirm(`Supprimer ${ids.length} article(s) ? Cette action est irréversible.`)) return;
    try {
      const r = await axios.post(`${API_URL}/api/invoicing/library/bulk-delete`, { ids }, { withCredentials: true });
      toast.success(`${r.data?.deleted ?? ids.length} article(s) supprimé(s)`);
      exitSelectMode();
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleBulkCategory = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      const r = await axios.post(
        `${API_URL}/api/invoicing/library/bulk-update-category`,
        { ids, category: bulkCategory === '__none__' ? '' : bulkCategory },
        { withCredentials: true },
      );
      toast.success(`${r.data?.updated ?? 0} article(s) re-catégorisé(s)`);
      exitSelectMode();
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur changement de catégorie');
    }
  };

  const handleRowDelete = async (it, ev) => {
    ev?.stopPropagation?.();
    if (!window.confirm(`Supprimer l'article "${it.description}" ?`)) return;
    try {
      await axios.delete(`${API_URL}/api/invoicing/library/${it.id}`, { withCredentials: true });
      toast.success('Article supprimé');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur suppression');
    }
  };

  const handleRowDuplicate = async (it, ev) => {
    ev?.stopPropagation?.();
    try {
      await axios.post(`${API_URL}/api/invoicing/library/${it.id}/duplicate`, {}, { withCredentials: true });
      toast.success('Article dupliqué');
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur duplication');
    }
  };

  const handleExportExcel = async (kind) => {
    try {
      const r = await axios.get(`${API_URL}/api/invoicing/library/export?kind=${kind}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bibliotheque_${kind}_${new Date().toISOString().slice(0,10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erreur export');
    }
  };

  const handleDownloadTemplate = async (kind) => {
    try {
      const r = await axios.get(`${API_URL}/api/imports/template?kind=${kind}`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `modele_${kind}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Erreur téléchargement modèle');
    }
  };

  const handleImportExcel = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API_URL}/api/imports/${kind}`, fd, { withCredentials: true });
      const { created = 0, updated = 0, errors = [] } = r.data || {};
      toast.success(`${created} créé(s), ${updated} mis à jour${errors.length ? `, ${errors.length} erreur(s)` : ''}`);
      if (errors.length) {
        console.warn('Import errors:', errors);
      }
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur import');
    }
  };

  const handleRefreshCoefs = async () => {
    if (!window.confirm("Mettre à jour les coefficients /m² sur tous les articles ? Les matériaux dont le coefficient a changé dans le catalogue seront ré-alignés. Pour les articles au m², la quantité sera également re-calculée.")) return;
    setRefreshingCoefs(true);
    try {
      const r = await axios.post(`${API_URL}/api/invoicing/library/refresh-coefficients`, {}, { withCredentials: true });
      const { items_updated, materials_updated } = r.data || {};
      toast.success(`${items_updated || 0} article(s), ${materials_updated || 0} matériau(x) mis à jour`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setRefreshingCoefs(false);
    }
  };

  const handleRenumber = async () => {
    if (!window.confirm("Renuméroter TOUS les articles selon leur catégorie ? Format : X.001, X.002, ... où X = numéro de catégorie. Les articles sans catégorie restent vides. Cette action est sûre et idempotente.")) return;
    try {
      const r = await axios.post(`${API_URL}/api/invoicing/library/renumber`, {}, { withCredentials: true });
      const { articles_updated = 0, by_category = {} } = r.data || {};
      const detail = Object.entries(by_category).map(([c, n]) => `${c}: ${n}`).join(' · ') || 'rien à mettre à jour';
      toast.success(`${articles_updated} article(s) renumérotés · ${detail}`);
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur renumérotation');
    }
  };

  const fetchData = async () => {
    try {
      const [r, c] = await Promise.all([
        axios.get(`${API_URL}/api/invoicing/library`, { withCredentials: true }),
        axios.get(`${API_URL}/api/invoicing/library/categories`, { withCredentials: true }),
      ]);
      setItems(r.data || []);
      setCategories(c.data || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { fetchData(); }, []);

  const filtered = useMemo(() => {
    const s = (search || '').toLowerCase();
    return (items || []).filter(it => {
      if (filterCat !== 'all') {
        if (filterCat === 'none' && it.category) return false;
        if (filterCat !== 'none' && it.category !== filterCat) return false;
      }
      if (!s) return true;
      return (it.description || '').toLowerCase().includes(s) ||
             (it.details || '').toLowerCase().includes(s) ||
             (it.category || '').toLowerCase().includes(s) ||
             (it.supplier || '').toLowerCase().includes(s);
    });
  }, [items, search, filterCat]);

  return (
    <div data-testid="project-library-tab" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="card" style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <BookOpen size={20} color="#002FA7" weight="bold" />
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700 }}>Bibliotheque d'articles</h3>
          <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>Reference globale - utilisable dans les devis du chantier {project?.name}</span>
          <button
            type="button"
            data-testid="refresh-coefs-btn"
            onClick={handleRefreshCoefs}
            disabled={refreshingCoefs}
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #002FA7', background: 'white', color: '#002FA7', borderRadius: 6, cursor: refreshingCoefs ? 'wait' : 'pointer', fontSize: '0.82rem', fontWeight: 600, opacity: refreshingCoefs ? 0.6 : 1 }}
            title="Re-aligner les coefficients /m² de tous les articles avec le catalogue"
          >
            <ArrowsClockwise size={14} weight="bold" />
            {refreshingCoefs ? 'Mise à jour…' : 'Mettre à jour les Coeff.'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 10, marginTop: 10, flexWrap: 'wrap' }}>
          <div style={{ position: 'relative', flex: '1 1 280px', minWidth: 240 }}>
            <MagnifyingGlass size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF', pointerEvents: 'none' }} />
            <input
              data-testid="project-lib-search"
              className="form-input"
              placeholder="Rechercher un article..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ paddingLeft: 36, fontSize: '0.85rem' }}
            />
          </div>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger data-testid="project-lib-cat-filter" style={{ width: 220, fontSize: '0.85rem' }}>
              <SelectValue placeholder="Toutes categories" />
            </SelectTrigger>
            <SelectContent className="z-[9999]">
              <SelectItem value="all">Toutes categories</SelectItem>
              <SelectItem value="none">Sans categorie</SelectItem>
              {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <button
            type="button"
            data-testid="export-articles-btn"
            onClick={() => handleExportExcel('articles')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #047857', background: 'white', color: '#047857', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
            title="Télécharger la bibliothèque articles en Excel"
          >
            <DownloadSimple size={14} weight="bold" />
            Export Excel
          </button>
          <label
            data-testid="import-articles-btn"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #002FA7', background: 'white', color: '#002FA7', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
            title="Importer une bibliothèque depuis Excel"
          >
            <UploadSimple size={14} weight="bold" />
            Importer
            <input
              type="file"
              accept=".xlsx"
              style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImportExcel('articles', f); e.target.value = ''; }}
            />
          </label>
          <button
            type="button"
            data-testid="download-articles-template-btn"
            onClick={() => handleDownloadTemplate('articles')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px dashed #6B7280', background: 'transparent', color: '#6B7280', borderRadius: 6, cursor: 'pointer', fontSize: '0.78rem', fontWeight: 500 }}
            title="Télécharger un modèle Excel vide"
          >
            Modèle
          </button>
          <button
            type="button"
            data-testid="select-mode-btn"
            onClick={() => { if (selectMode) exitSelectMode(); else setSelectMode(true); }}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid ' + (selectMode ? '#DC2626' : '#5E5F66'), background: selectMode ? '#FEE2E2' : 'white', color: selectMode ? '#DC2626' : '#1F2937', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
            title={selectMode ? 'Quitter le mode sélection' : 'Sélectionner plusieurs articles'}
          >
            {selectMode ? <X size={14} weight="bold" /> : <Check size={14} weight="bold" />}
            {selectMode ? 'Annuler la sélection' : 'Sélectionner'}
          </button>
          <button
            type="button"
            data-testid="manage-article-categories-btn"
            onClick={() => setShowCategoryManager(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #7C3AED', background: 'white', color: '#7C3AED', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
            title="Ajouter, renommer ou supprimer des catégories d'articles"
          >
            <Tag size={14} weight="bold" /> Gérer les catégories
          </button>
          <button
            type="button"
            data-testid="renumber-articles-btn"
            onClick={handleRenumber}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: '1px solid #0E7490', background: 'white', color: '#0E7490', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
            title="Renuméroter tous les articles selon leur catégorie (X.001, X.002, ...)"
          >
            <ArrowsClockwise size={14} weight="bold" /> Renuméroter
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => setShowMaterialForm(true)} data-testid="project-lib-new-material" style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Package size={14} /> Nouveau materiau
          </button>
          <button className="btn btn-primary btn-sm" onClick={() => { setEditing(null); setShowEditor(true); }} data-testid="project-lib-new-article">
            Nouvel article
          </button>
        </div>
      </div>

      {selectMode && selectedIds.size > 0 && (
        <div
          data-testid="bulk-actions-bar"
          style={{
            position: 'sticky', top: 0, zIndex: 5,
            background: '#1F2937', color: 'white', padding: '10px 14px',
            borderRadius: 8, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          }}
        >
          <strong style={{ fontSize: '0.9rem' }}>{selectedIds.size} sélectionné(s)</strong>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontSize: '0.78rem', color: '#D1D5DB' }}>Changer la catégorie :</span>
            <Select value={bulkCategory || ''} onValueChange={setBulkCategory}>
              <SelectTrigger data-testid="bulk-category-select" style={{ width: 200, fontSize: '0.85rem', height: 34, background: 'white', color: '#1F2937' }}>
                <SelectValue placeholder="Choisir..." />
              </SelectTrigger>
              <SelectContent className="z-[9999]">
                <SelectItem value="__none__">Sans catégorie</SelectItem>
                {categories.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <button
              type="button"
              data-testid="bulk-apply-category-btn"
              onClick={handleBulkCategory}
              disabled={!bulkCategory}
              style={{ padding: '6px 12px', border: 'none', background: bulkCategory ? '#3B82F6' : '#4B5563', color: 'white', borderRadius: 6, cursor: bulkCategory ? 'pointer' : 'not-allowed', fontSize: '0.82rem', fontWeight: 600 }}
            >Appliquer</button>
          </div>
          <button
            type="button"
            data-testid="bulk-delete-btn"
            onClick={handleBulkDelete}
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', border: 'none', background: '#DC2626', color: 'white', borderRadius: 6, cursor: 'pointer', fontSize: '0.82rem', fontWeight: 600 }}
          ><Trash size={14} weight="bold" /> Supprimer ({selectedIds.size})</button>
        </div>
      )}

      {filtered.length === 0 ? (
        <div className="empty-state">
          <Wrench size={48} className="empty-state-icon" />
          <p className="empty-state-text">Aucun article correspondant</p>
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="data-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                {selectMode && (
                  <th style={{ width: 40, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      data-testid="bulk-select-all"
                      checked={filtered.length > 0 && filtered.every(r => selectedIds.has(r.id))}
                      onChange={() => toggleSelectAll(filtered)}
                    />
                  </th>
                )}
                <th style={{ width: 80 }}>N°</th>
                <th>Designation</th>
                <th>Categorie</th>
                <th>Unite</th>
                <th style={{ textAlign: 'right' }}>Prix de revient</th>
                <th style={{ textAlign: 'right' }}>Marge</th>
                <th style={{ textAlign: 'right' }}>Prix de vente HT</th>
                <th style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(it => (
                <tr key={it.id}
                  data-testid={`project-lib-row-${it.id}`}
                  style={{ cursor: selectMode ? 'default' : 'pointer', background: selectedIds.has(it.id) ? '#EFF6FF' : undefined }}
                  onClick={() => {
                    if (selectMode) { toggleSelectOne(it.id); return; }
                    setEditing(it); setShowEditor(true);
                  }}
                  title={selectMode ? 'Cliquer pour sélectionner' : 'Cliquer pour modifier'}
                >
                  {selectMode && (
                    <td style={{ textAlign: 'center' }} onClick={(e) => { e.stopPropagation(); toggleSelectOne(it.id); }}>
                      <input
                        type="checkbox"
                        data-testid={`bulk-select-${it.id}`}
                        checked={selectedIds.has(it.id)}
                        onChange={() => toggleSelectOne(it.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                  )}
                  <td style={{ fontFamily: 'monospace', fontSize: '0.82rem', fontWeight: 700, color: '#1D4ED8' }} data-testid={`article-num-${it.id}`}>
                    {it.article_number || <span style={{ color: '#C5C7CD' }}>-</span>}
                  </td>
                  <td style={{ fontWeight: 600 }}>
                    {it.description}
                    {it.details ? <div style={{ fontSize: '0.74rem', color: '#9CA3AF', fontWeight: 400 }}>{it.details}</div> : null}
                  </td>
                  <td>{it.category ? <span style={{ padding: '2px 8px', background: '#EFF6FF', color: '#1D4ED8', borderRadius: 4, fontSize: '0.74rem', fontWeight: 600 }}>{it.category}</span> : <span style={{ color: '#C5C7CD' }}>-</span>}</td>
                  <td>{it.unit}</td>
                  <td style={{ textAlign: 'right', color: '#5E5F66' }}>{(it.cost_price_ht || 0).toFixed(2)} EUR</td>
                  <td style={{ textAlign: 'right' }}>{it.margin_percent ?? 30}%</td>
                  <td style={{ textAlign: 'right', fontWeight: 700, color: '#047857' }}>{(it.unit_price || 0).toFixed(2)} EUR</td>
                  <td style={{ textAlign: 'center' }} onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      data-testid={`row-duplicate-${it.id}`}
                      onClick={(e) => handleRowDuplicate(it, e)}
                      title="Dupliquer cet article"
                      style={{ padding: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: '#1D4ED8', borderRadius: 4, marginRight: 4 }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#EFF6FF'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <Copy size={16} weight="bold" />
                    </button>
                    <button
                      type="button"
                      data-testid={`row-delete-${it.id}`}
                      onClick={(e) => handleRowDelete(it, e)}
                      title="Supprimer cet article"
                      style={{ padding: 4, border: 'none', background: 'transparent', cursor: 'pointer', color: '#DC2626', borderRadius: 4 }}
                      onMouseEnter={(e) => e.currentTarget.style.background = '#FEE2E2'}
                      onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                    >
                      <Trash size={16} weight="bold" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ArticleEditorModal
        show={showEditor}
        item={editing}
        onClose={() => { setShowEditor(false); setEditing(null); }}
        onSaved={() => fetchData()}
      />
      <MaterialFormModal
        show={showMaterialForm}
        categories={categories}
        onClose={() => setShowMaterialForm(false)}
        onSaved={() => fetchData()}
      />
      <CategoryManagerModal
        open={showCategoryManager}
        onClose={() => setShowCategoryManager(false)}
        kind="articles"
        onChange={() => fetchData()}
      />
    </div>
  );
};

export default ProjectLibraryTab;
